import React, {Fragment, useEffect, useState} from "react";
import {
    Button,
    Col,
    Form,
    Row,
    Edit,
    ListButton,
    RefreshButton,
    useModal,
    useForm,
} from "@pankod/refine-antd";
import {IResourceComponentsProps} from "@pankod/refine-core";
import {IProductBundle, Product} from "Interfaces";
import {ProductModal} from "./Components/ProductModal";
import {CodeFormItem, NameFormItem, StatusFormItem, SlugFormItem, DescriptionFormItem} from "./Components/FormItems";
import {CoverImageSection} from "./Components/CoverImageSection";
import {MemberProductsTable} from "./Components/MemberProductsTable";
import {useBundleImage} from "./Components/useBundleImage";
import {getBestImageUrl} from "./Components/imageUrl";
import {withGeneratedBundleSlug} from "./Components/slug";
import {syncBundlesToMeilisearch} from "../../Services/productServices";
import toast from "react-hot-toast";

const REACT_APP_STOREFRONT_URL = process.env.REACT_APP_STOREFRONT_URL || "http://localhost:3000";

export const ProductBundleEdit: React.FC<IResourceComponentsProps> = () => {
    const {show: showProductModal, close: closeProductModal, modalProps} = useModal();
    const {formProps, saveButtonProps, form, queryResult} = useForm<IProductBundle>({
        resource: "product-bundles",
        action: "edit",
    });

    const {data} = queryResult;
    const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
    const [quantities, setQuantities] = useState<{ [key: string]: number }>({});

    const img = useBundleImage({products: selectedProducts, bundleId: data?.data?.id, form});
    const {setCoverImageUrl} = img;

    useEffect(() => {
        if (data) {
            setSelectedProducts(data.data.products);
            form.setFieldsValue({products: data.data.products.map(product => product.id)});
            form.setFieldsValue({status: data.data.status || "draft"});
            form.setFieldsValue({slug: data.data.slug});

            // Load cover image
            if (data.data.coverImage) {
                setCoverImageUrl(getBestImageUrl(data.data.coverImage));
                form.setFieldsValue({coverImage: data.data.coverImage.id});
            }

            // Prefill quantities from quantityTable
            if (data.data.quantityTable) {
                if (typeof data.data.quantityTable === "string") {
                    try {
                        setQuantities(JSON.parse(data.data.quantityTable));
                    } catch {
                        setQuantities({});
                    }
                } else {
                    setQuantities(data.data.quantityTable as Record<string, number>);
                }
            }
        }
    }, [data, form, setCoverImageUrl]);

    useEffect(() => {
        form.setFieldsValue({products: selectedProducts?.map(product => product.id)});
    }, [selectedProducts, form]);

    const handleQuantityChange = (sku: string, value: number) => {
        if (value < 1) return;
        const newQuantities = {...quantities, [sku]: value};
        setQuantities(newQuantities);
        form.setFieldsValue({quantityTable: JSON.stringify(newQuantities)});
    };

    const bundleSlug = data?.data?.slug;
    const storeUrl = bundleSlug ? `${REACT_APP_STOREFRONT_URL}/bundle/${bundleSlug}` : undefined;

    const handleSyncToMeilisearch = () => {
        toast.promise(syncBundlesToMeilisearch(), {
            loading: "Syncing bundles to Meilisearch...",
            success: (resp: any) => resp?.data?.message || "Bundles synced to Meilisearch",
            error: (err) => `Sync failed: ${err?.message || err}`,
        });
    };

    return (
        <Edit
            saveButtonProps={saveButtonProps}
            pageHeaderProps={{
                extra: (
                    <Fragment>
                        <ListButton />
                        <RefreshButton />
                        <Button onClick={handleSyncToMeilisearch}>Sync to Meilisearch</Button>
                        {storeUrl && (
                            <Button
                                type="link"
                                href={storeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                View in Store
                            </Button>
                        )}
                    </Fragment>
                ),
            }}
        >
            <ProductModal
                modalProps={modalProps}
                closeProductModal={closeProductModal}
                setSelectedIds={setSelectedProducts}
                initialSelectedIds={selectedProducts}
            />

            <Form {...formProps} onFinish={withGeneratedBundleSlug(formProps.onFinish)} layout="vertical">
                <Row gutter={{xs: 8, sm: 16, md: 24, lg: 32}}>
                    <Col><NameFormItem/></Col>
                    <Col><CodeFormItem/></Col>
                    <Col><StatusFormItem/></Col>
                    <Col><SlugFormItem/></Col>
                </Row>

                <Row gutter={24}>
                    <Col span={12}>
                        <DescriptionFormItem/>
                    </Col>
                    <Col span={12}>
                        <CoverImageSection
                            coverImageUrl={img.coverImageUrl}
                            uploading={img.uploading}
                            generating={img.generating}
                            aiPrompt={img.aiPrompt}
                            setAiPrompt={img.setAiPrompt}
                            showAiPrompt={img.showAiPrompt}
                            setShowAiPrompt={img.setShowAiPrompt}
                            buildDefaultPrompt={img.buildDefaultPrompt}
                            handleUpload={img.handleUpload}
                            handleGenerateImage={img.handleGenerateImage}
                            canGenerate={!!data?.data?.id && !!selectedProducts?.length}
                        />
                        <Form.Item name="coverImage" hidden>
                            <input type="hidden"/>
                        </Form.Item>
                    </Col>
                </Row>

                <MemberProductsTable
                    selectedProducts={selectedProducts}
                    quantities={quantities}
                    handleQuantityChange={handleQuantityChange}
                    onRemove={(record) => setSelectedProducts(selectedProducts.filter(p => p.id !== record.id))}
                    onAssign={showProductModal}
                />

                <Form.Item name="products" hidden>
                    <input type="hidden"/>
                </Form.Item>
                <Form.Item name="quantityTable" hidden>
                    <input type="hidden"/>
                </Form.Item>
            </Form>
        </Edit>
    );
};
